import type { MRT_VisibilityState } from "material-react-table";
import { createContext } from "react";

/**
 * Where a table finds the reader's column choices kept on the server (#2788).
 *
 * Its own module, apart from the provider that fills it, so that the table wrapper depends on this
 * contract and not on the store and API behind it: a table drawn without the provider — every spec
 * that renders one — simply has no server to talk to and keeps its choice in this browser alone.
 */
export type TableColumnPreferences = {
  /**
   * Every table the reader has a saved choice for, once the server has answered. `undefined` while
   * nobody is signed in, while the answer is on its way, or when the server could not give one —
   * the three cases in which the browser's own copy is the best there is.
   */
  savedTables: ReadonlyMap<string, MRT_VisibilityState> | undefined;
  /** Saves one table's whole choice for the signed-in reader. Fire and forget: a failure is logged. */
  saveTable: (tableKey: string, visibility: MRT_VisibilityState) => void;
  /**
   * Asks the server for the reader's choices again, unless it answered moments ago. Called when a
   * table opens, so a choice made or reset in another browser reaches this one.
   */
  refresh?: () => void;
  /**
   * The column widths and column order of each table's current view (#6714), once the server has
   * answered; `undefined` in the same three cases as `savedTables`.
   */
  savedLayouts?: ReadonlyMap<string, ColumnLayout>;
  /**
   * Keeps one changed part of a table's layout in its current view, as the view at the current
   * location. A part left out keeps the stored one. Fire and forget: a failure is logged.
   */
  saveLayout?: (tableKey: string, part: ColumnLayout) => void;
  /**
   * How many times a saved view was opened on each table in this tab (#6717). A table already on
   * screen keeps the reader's own column choice over the account's; when this changes, it takes the
   * account's again, which is the view just opened.
   */
  viewRevisions?: ReadonlyMap<string, number>;
  /** Tells the tables keyed `tableKey` that a view was opened, so they show its columns. */
  openedView?: (tableKey: string) => void;
};

/** A reader's column widths and column order, as a table's current view keeps them. */
export type ColumnLayout = Readonly<{
  columnSizing?: Readonly<Record<string, number>>;
  columnOrder?: readonly string[];
}>;

export const TableColumnPreferencesContext = createContext<TableColumnPreferences | undefined>(undefined);

/**
 * The columns the reader chose for the table being drawn, for the controls inside it.
 *
 * Provided by `MRTNoMemo` around the table, so that the Show/Hide columns button in any toolbar can
 * tell whether the reader has put columns away. `undefined` for a table that keeps no choice.
 */
export const ReaderColumnChoiceContext = createContext<MRT_VisibilityState | undefined>(undefined);
